import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import API from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const STATUS_STEPS = [
  { key: 'placed',    icon: '🛍️', label: 'Order Placed' },
  { key: 'confirmed', icon: '✅', label: 'Confirmed' },
  { key: 'shipped',   icon: '📦', label: 'Shipped' },
  { key: 'transit',   icon: '🚚', label: 'In Transit' },
  { key: 'delivered', icon: '🏠', label: 'Delivered' },
];

function stepIndex(status) {
  if (['delivered','completed'].includes(status)) return 4;
  if (status === 'shipped') return 3;
  if (['confirmed','awaiting_shipment'].includes(status)) return 1;
  return 0;
}

function carrierStepIndex(carrierStatus) {
  if (!carrierStatus) return -1;
  const s = carrierStatus.toLowerCase();
  if (s.includes('delivered')) return 4;
  if (s.includes('out_for_delivery') || s.includes('out for delivery')) return 3;
  if (s.includes('in_transit') || s.includes('in transit')) return 3;
  if (s.includes('pre_transit') || s.includes('pre transit')) return 2;
  return 2;
}

function StatusBadge({ status }) {
  const map = {
    pending:            { bg: '#FFF9E6', color: '#C9930A', label: 'Processing' },
    confirmed:          { bg: '#EAF5EC', color: '#2D7A3A', label: 'Confirmed' },
    awaiting_shipment:  { bg: '#EBF0FF', color: '#1A5276', label: 'Awaiting Shipment' },
    shipped:            { bg: '#EBF0FF', color: '#1A5276', label: 'In Transit' },
    delivered:          { bg: '#EAF5EC', color: '#2D7A3A', label: 'Delivered' },
    completed:          { bg: '#EAF5EC', color: '#2D7A3A', label: 'Completed' },
    cancelled:          { bg: '#F7F7F7', color: '#888',    label: 'Cancelled' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ fontSize: 13, fontWeight: 700, padding: '6px 14px', borderRadius: 100, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

export default function TrackOrderPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [orderNumber, setOrderNumber] = useState(params.get('order') || '');
  const [email, setEmail]             = useState(params.get('email') || '');
  const [order, setOrder]             = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [carrierInfo, setCarrierInfo] = useState(null);
  const [carrierLoading, setCarrierLoading] = useState(false);

  // Auto-search if params were passed in URL
  useEffect(() => {
    if (params.get('order') && params.get('email')) {
      doTrack(params.get('order'), params.get('email'));
    }
  }, []);

  const doTrack = async (num, eml) => {
    setLoading(true); setError(''); setCarrierInfo(null);
    try {
      const { data } = await API.get(`/orders/track/${num}`, { params: { email: eml } });
      setOrder(data.order);
      if (data.order.tracking_number) fetchCarrier(data.order.tracking_number);
    } catch (err) {
      setError(err.response?.data?.error || 'Order not found. Please check your details.');
      setOrder(null);
    }
    setLoading(false);
  };

  const handleTrack = (e) => { e.preventDefault(); doTrack(orderNumber, email); };

  const fetchCarrier = async (trackingNum) => {
    setCarrierLoading(true);
    try {
      const { data } = await API.get(`/shipping/track/${trackingNum}`);
      setCarrierInfo(data);
    } catch { /* silent — carrier info is bonus */ }
    setCarrierLoading(false);
  };

  const activeStep = order
    ? Math.max(stepIndex(order.status), carrierInfo ? carrierStepIndex(carrierInfo.status) : -1)
    : 0;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px 80px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, marginBottom: 8 }}>
          Track Your Order
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text-soft)', maxWidth: 400, margin: '0 auto' }}>
          Enter your order number and email to track your purchase
        </p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleTrack} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 28, marginBottom: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Order Number</label>
            <input className="form-input" placeholder="ORD-12345678"
              value={orderNumber} onChange={e => { setOrderNumber(e.target.value.toUpperCase()); setError(''); }}
              disabled={loading} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email Address</label>
            <input className="form-input" type="email" placeholder="you@example.com"
              value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
              disabled={loading} />
          </div>
        </div>
        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        <button type="submit" className="btn btn-cherry btn-full"
          disabled={loading || !orderNumber || !email}>
          {loading ? '⏳ Tracking…' : '🔍 Track Order'}
        </button>
      </form>

      {order && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Status header */}
          <div style={{ background: 'white', border: '2px solid var(--cherry)', borderRadius: 16, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Order Number</div>
                <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: 'var(--cherry)' }}>{order.order_number}</div>
              </div>
              <StatusBadge status={order.status} />
            </div>

            {/* Progress bar */}
            <div style={{ position: 'relative' }}>
              {/* connector line */}
              <div style={{ position: 'absolute', top: 20, left: '10%', right: '10%', height: 4, background: 'var(--border)', borderRadius: 4, zIndex: 0 }} />
              <div style={{ position: 'absolute', top: 20, left: '10%', height: 4, background: 'var(--cherry)', borderRadius: 4, zIndex: 1,
                width: `${Math.min(activeStep / (STATUS_STEPS.length - 1) * 80, 80)}%`, transition: 'width 0.5s ease' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
                {STATUS_STEPS.map((step, i) => {
                  const done    = i <= activeStep;
                  const current = i === activeStep;
                  return (
                    <div key={step.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 1 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: done ? 'var(--cherry)' : 'white',
                        border: `3px solid ${done ? 'var(--cherry)' : 'var(--border)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: done ? 16 : 18,
                        boxShadow: current ? '0 0 0 4px rgba(192,57,43,0.15)' : 'none',
                        transition: 'all 0.3s',
                      }}>
                        {done ? '✓' : step.icon}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: done ? 700 : 400, color: done ? 'var(--text-primary)' : 'var(--text-soft)', textAlign: 'center' }}>
                        {step.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Dates row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Order Date</div>
                <div style={{ fontWeight: 600 }}>{format(new Date(order.created_at), 'MMM d, yyyy')}</div>
              </div>
              {order.shipped_at && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Shipped</div>
                  <div style={{ fontWeight: 600 }}>{format(new Date(order.shipped_at), 'MMM d, yyyy')}</div>
                </div>
              )}
              {carrierInfo?.estimatedDelivery && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Est. Delivery</div>
                  <div style={{ fontWeight: 600, color: '#2D7A3A' }}>{format(new Date(carrierInfo.estimatedDelivery), 'MMM d, yyyy')}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Total</div>
                <div style={{ fontWeight: 600, color: 'var(--cherry)' }}>${parseFloat(order.total_charged).toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Item */}
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Item</div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: 72, height: 72, borderRadius: 10, background: 'var(--cream)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
                {order.primary_image
                  ? <img src={`${API_BASE}${order.primary_image}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : '📦'}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{order.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>${parseFloat(order.item_price).toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Tracking / carrier info */}
          {order.tracking_number && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>📦 Shipment</div>

              <div style={{ background: '#EBF5FF', borderRadius: 10, padding: '14px 16px', marginBottom: carrierInfo?.details?.length ? 16 : 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1A5276', letterSpacing: 1, marginBottom: 4 }}>TRACKING NUMBER</div>
                <div style={{ fontFamily: 'monospace', fontSize: 17, fontWeight: 700, color: '#1A5276' }}>{order.tracking_number}</div>
                {order.shipping_carrier && (
                  <div style={{ fontSize: 12, color: '#1A5276', marginTop: 3 }}>via {order.shipping_carrier}</div>
                )}
              </div>

              {/* Live carrier events */}
              {carrierLoading && (
                <div style={{ fontSize: 13, color: 'var(--text-soft)', padding: '8px 0' }}>⏳ Loading carrier updates…</div>
              )}
              {carrierInfo?.details?.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Carrier Updates</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {[...carrierInfo.details].reverse().map((d, i) => (
                      <div key={i} style={{ display: 'flex', gap: 14, paddingBottom: 14 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: i === 0 ? 'var(--cherry)' : 'var(--border)', flexShrink: 0, marginTop: 4 }} />
                          {i < carrierInfo.details.length - 1 && (
                            <div style={{ width: 2, flex: 1, background: 'var(--border)', minHeight: 20 }} />
                          )}
                        </div>
                        <div style={{ paddingBottom: 4 }}>
                          <div style={{ fontSize: 13, fontWeight: i === 0 ? 700 : 400 }}>
                            {d.message || d.status?.replace(/_/g, ' ')}
                          </div>
                          {(d.city || d.state) && (
                            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{[d.city, d.state].filter(Boolean).join(', ')}</div>
                          )}
                          {d.datetime && (
                            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 2 }}>
                              {format(new Date(d.datetime), 'MMM d, h:mm a')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {carrierInfo && !carrierInfo.details?.length && !carrierLoading && (
                <div style={{ fontSize: 13, color: 'var(--text-soft)', marginTop: 10 }}>No carrier scan events yet — check back once the package is picked up.</div>
              )}
            </div>
          )}

          {/* Ship-to address */}
          {order.ship_to_name && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>📍 Shipping To</div>
              <div style={{ fontSize: 14, color: 'var(--text-mid)', lineHeight: 1.8 }}>
                <div style={{ fontWeight: 600 }}>{order.ship_to_name}</div>
                {order.ship_to_address && <div>{order.ship_to_address}</div>}
                <div>{[order.ship_to_city, order.ship_to_state, order.ship_to_zip].filter(Boolean).join(', ')}</div>
              </div>
            </div>
          )}

          <button className="btn btn-ghost" onClick={() => navigate(-1)}>← Back</button>
        </div>
      )}
    </div>
  );
}
